import React, { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import TopBar from "../../components/TopBar";
import {
  AlertRule,
  AlertTrigger,
  deleteAlertRule,
  listAlertRules,
  saveAlertRule,
} from "./alertsService";
import "./Alerts.css";

type Props = { user?: any; onSignOut?: () => void };
type Camera = { cameraId?: string; SK?: string; name?: string; enabled?: boolean };

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://f0usr04663.execute-api.us-east-1.amazonaws.com";
const TRIGGERS: Array<{ value: AlertTrigger; label: string }> = [
  { value: "motion", label: "Motion" },
  { value: "person", label: "Person" },
  { value: "car", label: "Car" },
  { value: "bicycle", label: "Bicycle" },
  { value: "animal", label: "Animal" },
  { value: "boat", label: "Boat" },
  { value: "loitering", label: "Loitering" },
];

function getEmail(user: any) {
  return user?.signInDetails?.loginId || user?.attributes?.email || user?.username || "";
}
function cameraId(camera: Camera) {
  return String(camera.cameraId || camera.SK || "").replace(/^CAMERA#/, "").trim();
}
function cameraName(camera: Camera) {
  const id = cameraId(camera);
  return String(camera.name || (id ? `Camera ${id}` : "Unnamed camera"));
}
function normalizeEmails(value: string) {
  return Array.from(new Set(value.split(/[\n,;]+/).map((item) => item.trim().toLowerCase()).filter(Boolean)));
}
function filterLabel(minutes: number) {
  if (!minutes) return "No filtering";
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  if (!remainder) return `${hours} h`;
  return `${hours} h ${remainder} min`;
}
function clampFilterMinutes(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(720, Math.max(0, Math.round(value / 5) * 5));
}

const EMPTY_FORM = {
  alertRuleId: "",
  name: "",
  cameraIds: [] as string[],
  triggers: [] as AlertTrigger[],
  emailRecipients: "",
  notificationFilterMinutes: 5,
  enabled: true,
};

export default function Alerts({ user, onSignOut }: Props) {
  const email = getEmail(user);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [recipientDraft, setRecipientDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error("Your session has expired. Sign in again.");
      const cameraResponse = await fetch(`${API_BASE}/cameras`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const cameraText = await cameraResponse.text();
      let cameraPayload: any = {};
      try { cameraPayload = cameraText ? JSON.parse(cameraText) : {}; } catch { cameraPayload = {}; }
      if (!cameraResponse.ok) throw new Error(cameraPayload?.message || `Unable to load cameras (${cameraResponse.status}).`);
      setCameras(Array.isArray(cameraPayload?.items) ? cameraPayload.items : []);
      setRules(await listAlertRules());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load alerts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const cameraLookup = useMemo(() => new Map(cameras.map((camera) => [cameraId(camera), cameraName(camera)])), [cameras]);
  const recipients = useMemo(() => normalizeEmails(form.emailRecipients), [form.emailRecipients]);

  function newRule() {
    setForm({ ...EMPTY_FORM, cameraIds: [], triggers: [] });
    setRecipientDraft("");
    setNotice("");
    setError("");
    setFormOpen(true);
  }
  function editRule(rule: AlertRule) {
    setForm({
      alertRuleId: rule.alertRuleId,
      name: rule.name,
      cameraIds: [...rule.cameraIds],
      triggers: [...rule.triggers],
      emailRecipients: rule.emailRecipients.join("\n"),
      notificationFilterMinutes: Number(rule.notificationFilterMinutes ?? 5),
      enabled: rule.enabled !== false,
    });
    setRecipientDraft("");
    setNotice("");
    setError("");
    setFormOpen(true);
  }
  function toggleCamera(id: string) {
    setForm((current) => ({ ...current, cameraIds: current.cameraIds.includes(id) ? current.cameraIds.filter((item) => item !== id) : [...current.cameraIds, id] }));
  }
  function toggleTrigger(trigger: AlertTrigger) {
    setForm((current) => ({ ...current, triggers: current.triggers.includes(trigger) ? current.triggers.filter((item) => item !== trigger) : [...current.triggers, trigger] }));
  }
  function addRecipient() {
    const candidate = recipientDraft.trim().toLowerCase();
    if (!candidate) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) {
      setError(`Enter a valid email address: ${candidate}`);
      return;
    }
    if (!recipients.includes(candidate)) {
      setForm((current) => ({ ...current, emailRecipients: [...normalizeEmails(current.emailRecipients), candidate].join("\n") }));
    }
    setRecipientDraft("");
    setError("");
  }
  function removeRecipient(address: string) {
    setForm((current) => ({ ...current, emailRecipients: normalizeEmails(current.emailRecipients).filter((item) => item !== address).join("\n") }));
  }
  function recipientKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === "," || event.key === ";") {
      event.preventDefault();
      addRecipient();
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!form.name.trim()) return setError("Enter an alert name.");
    if (!form.cameraIds.length) return setError("Select at least one camera.");
    if (!form.triggers.length) return setError("Select at least one alert event.");
    if (!recipients.length) return setError("Add at least one email recipient.");
    setSaving(true);
    try {
      const saved = await saveAlertRule({
        alertRuleId: form.alertRuleId || undefined,
        name: form.name.trim(),
        cameraIds: form.cameraIds,
        triggers: form.triggers,
        emailRecipients: recipients,
        notificationFilterMinutes: clampFilterMinutes(form.notificationFilterMinutes),
        enabled: form.enabled,
      });
      setRules((current) => current.some((rule) => rule.alertRuleId === saved.alertRuleId)
        ? current.map((rule) => rule.alertRuleId === saved.alertRuleId ? saved : rule)
        : [saved, ...current]);
      setFormOpen(false);
      setForm({ ...EMPTY_FORM, cameraIds: [], triggers: [] });
      setRecipientDraft("");
      setNotice("Alert rule saved.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save the alert rule.");
    } finally { setSaving(false); }
  }

  async function remove(rule: AlertRule) {
    if (!window.confirm(`Delete alert rule “${rule.name}”?`)) return;
    setError("");
    try {
      await deleteAlertRule(rule.alertRuleId);
      setRules((current) => current.filter((item) => item.alertRuleId !== rule.alertRuleId));
      setNotice("Alert rule deleted.");
    } catch (deleteError) { setError(deleteError instanceof Error ? deleteError.message : "Unable to delete the alert rule."); }
  }
  async function toggleEnabled(rule: AlertRule) {
    setError("");
    try {
      const saved = await saveAlertRule({
        alertRuleId: rule.alertRuleId,
        name: rule.name,
        cameraIds: rule.cameraIds,
        triggers: rule.triggers,
        emailRecipients: rule.emailRecipients,
        notificationFilterMinutes: Number(rule.notificationFilterMinutes ?? 5),
        enabled: !rule.enabled,
      });
      setRules((current) => current.map((item) => item.alertRuleId === saved.alertRuleId ? saved : item));
    } catch (toggleError) { setError(toggleError instanceof Error ? toggleError.message : "Unable to update the alert rule."); }
  }

  const selectedCameraText = form.cameraIds.length === 0 ? "Select cameras" : form.cameraIds.length === 1 ? cameraLookup.get(form.cameraIds[0]) || `Camera ${form.cameraIds[0]}` : `${form.cameraIds.length} cameras selected`;
  const selectedTriggerText = form.triggers.length === 0 ? "Select events" : form.triggers.length === 1 ? TRIGGERS.find((item) => item.value === form.triggers[0])?.label || form.triggers[0] : `${form.triggers.length} events selected`;

  return (
    <div className="alerts-page">
      <TopBar title="Qumra" onSignOut={onSignOut} email={email} />
      <main className="alerts-page__content">
        <section className="alerts-heading">
          <div><p className="alerts-eyebrow">Notifications</p><h1>Alerts</h1><p>Configure email notifications from the motion and analytics events Qumra already produces.</p></div>
          <button type="button" className="alerts-primary" onClick={newRule}>+ Add alert</button>
        </section>
        {error && <div className="alerts-message alerts-message--error" role="alert"><div><strong>Alerts could not be loaded.</strong><span>{error}</span></div><button type="button" onClick={() => void load()}>Retry</button></div>}
        {notice && <div className="alerts-message alerts-message--success">{notice}</div>}
        {loading ? <section className="alerts-empty"><div className="alerts-loader" />Loading alert rules…</section> : rules.length === 0 ? (
          <section className="alerts-empty alerts-empty--large"><div className="alerts-empty__icon">✉</div><h2>No alert rules yet</h2><p>Create an alert rule to email selected recipients when Qumra receives a matching event.</p><button type="button" className="alerts-primary" onClick={newRule}>Create alert</button></section>
        ) : <section className="alerts-rule-list">{rules.map((rule) => (
          <article className="alerts-rule-card" key={rule.alertRuleId}>
            <div className="alerts-rule-card__main"><div className="alerts-rule-card__icon">✉</div><div><div className="alerts-rule-card__title-row"><h2>{rule.name}</h2><span className={rule.enabled ? "alerts-badge alerts-badge--active" : "alerts-badge"}>{rule.enabled ? "Active" : "Disabled"}</span></div><div className="alerts-rule-card__meta"><span>{rule.cameraIds.length} camera{rule.cameraIds.length === 1 ? "" : "s"}</span><span>•</span><span>{rule.triggers.map((trigger) => TRIGGERS.find((item) => item.value === trigger)?.label || trigger).join(", ")}</span><span>•</span><span>Filter: {filterLabel(Number(rule.notificationFilterMinutes ?? 5))}</span></div><div className="alerts-rule-card__recipients">{rule.emailRecipients.join(", ")}</div></div></div>
            <div className="alerts-rule-card__actions"><button type="button" className="alerts-secondary" onClick={() => void toggleEnabled(rule)}>{rule.enabled ? "Disable" : "Enable"}</button><button type="button" className="alerts-secondary" onClick={() => editRule(rule)}>Edit</button><button type="button" className="alerts-icon-danger" onClick={() => void remove(rule)} aria-label={`Delete ${rule.name}`}>×</button></div>
          </article>
        ))}</section>}
      </main>

      {formOpen && <div className="alerts-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setFormOpen(false); }}>
        <form className="alerts-dialog" onSubmit={submit}>
          <div className="alerts-dialog__header"><div><p className="alerts-eyebrow">Alert configuration</p><h2>{form.alertRuleId ? "Edit alert" : "Create alert"}</h2><p>Choose the cameras and existing Qumra events that should generate email notifications.</p></div><button type="button" className="alerts-close" disabled={saving} onClick={() => setFormOpen(false)}>×</button></div>

          <section className="alerts-section">
            <div className="alerts-section__heading"><span className="alerts-step">1</span><div><h3>Alert details</h3><p>Name the rule and select where it applies.</p></div></div>
            <label className="alerts-field"><span>Alert name</span><input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Front entrance alerts" /></label>
            <div className="alerts-field"><span>Cameras</span><details className="alerts-dropdown"><summary><span>{selectedCameraText}</span><span className="alerts-chevron">⌄</span></summary><div className="alerts-dropdown__menu">{cameras.length === 0 ? <div className="alerts-dropdown__empty">No cameras available.</div> : cameras.map((camera) => { const id = cameraId(camera); return <label className="alerts-dropdown__option" key={id}><input type="checkbox" checked={form.cameraIds.includes(id)} onChange={() => toggleCamera(id)} /><span><strong>{cameraName(camera)}</strong><small>{camera.enabled === false ? "Disabled" : `Camera ${id}`}</small></span></label>; })}</div></details>{form.cameraIds.length > 0 && <div className="alerts-chips">{form.cameraIds.map((id) => <button type="button" className="alerts-chip" key={id} onClick={() => toggleCamera(id)}>{cameraLookup.get(id) || `Camera ${id}`} <span>×</span></button>)}</div>}</div>
            <div className="alerts-field"><span>Alert events</span><details className="alerts-dropdown"><summary><span>{selectedTriggerText}</span><span className="alerts-chevron">⌄</span></summary><div className="alerts-dropdown__menu">{TRIGGERS.map((trigger) => <label className="alerts-dropdown__option" key={trigger.value}><input type="checkbox" checked={form.triggers.includes(trigger.value)} onChange={() => toggleTrigger(trigger.value)} /><span><strong>{trigger.label}</strong><small>Existing Qumra {trigger.label.toLowerCase()} event</small></span></label>)}</div></details>{form.triggers.length > 0 && <div className="alerts-chips">{form.triggers.map((trigger) => <button type="button" className="alerts-chip" key={trigger} onClick={() => toggleTrigger(trigger)}>{TRIGGERS.find((item) => item.value === trigger)?.label || trigger} <span>×</span></button>)}</div>}</div>
          </section>

          <section className="alerts-section">
            <div className="alerts-section__heading"><span className="alerts-step">2</span><div><h3>Email notifications</h3><p>Choose who receives the notification and how repeated alerts are filtered.</p></div></div>
            <div className="alerts-field"><span>Alert recipients</span><div className="alerts-recipient-entry"><input type="email" value={recipientDraft} onChange={(event) => setRecipientDraft(event.target.value)} onKeyDown={recipientKeyDown} placeholder="security@example.com" /><button type="button" className="alerts-recipient-add" onClick={addRecipient}>Add email</button></div>{recipients.length > 0 && <div className="alerts-recipient-chips">{recipients.map((address) => <button type="button" className="alerts-recipient-chip" key={address} onClick={() => removeRecipient(address)}>{address} <span>×</span></button>)}</div>}<small>Type an email address and press Add email or Enter. Click a recipient to remove it.</small></div>
            <label className="alerts-field"><span>Notifications filtering</span><div className="alerts-filter-row alerts-filter-row--stepper"><span>Do not send notifications that occur within</span><div className="alerts-number-wrap"><input type="number" min={0} max={720} step={5} value={form.notificationFilterMinutes} onChange={(event) => setForm((current) => ({ ...current, notificationFilterMinutes: clampFilterMinutes(Number(event.target.value)) }))} onBlur={(event) => setForm((current) => ({ ...current, notificationFilterMinutes: clampFilterMinutes(Number(event.target.value)) }))} aria-label="Notification filtering minutes" /><span>min</span></div><span>of the last notification.</span></div><small>Use the keyboard or the up/down arrows. Range: 0 to 720 minutes (12 hours), in 5-minute steps. The filter is tracked independently for each camera in this alert rule.</small></label>
          </section>

          <label className="alerts-enabled"><span><strong>Alert enabled</strong><small>Matching events can send email while this rule is enabled.</small></span><input type="checkbox" checked={form.enabled} onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} /></label>
          <div className="alerts-dialog__footer"><button type="button" className="alerts-secondary" disabled={saving} onClick={() => setFormOpen(false)}>Cancel</button><button type="submit" className="alerts-primary" disabled={saving}>{saving ? "Saving…" : "Save alert"}</button></div>
        </form>
      </div>}
    </div>
  );
}
